import { useState, useCallback, useEffect, useRef } from 'react';
import {
  createReferenceEvaluation,
  getReferenceEvaluation,
  ReferenceEvaluationFromApi,
} from '../api';

type ReferenceEvaluationState = {
  evaluation: ReferenceEvaluationFromApi | null;
  isUploading: boolean;
  isPolling: boolean;
  error: string | null;
};

type UseReferenceEvaluationOptions = {
  initialEvaluationId?: number;
  pollInterval?: number;
  onComplete?: (evaluation: ReferenceEvaluationFromApi) => void;
  onError?: (error: string) => void;
};

const COMPLETED_STATUSES = ['completed', 'error'];
const DEFAULT_POLL_INTERVAL = 2000;

export function useReferenceEvaluation(
  options: UseReferenceEvaluationOptions = {},
) {
  const {
    initialEvaluationId,
    pollInterval = DEFAULT_POLL_INTERVAL,
    onComplete,
    onError,
  } = options;

  const [state, setState] = useState<ReferenceEvaluationState>({
    evaluation: null,
    isUploading: false,
    isPolling: false,
    error: null,
  });

  const pollTimeoutRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const pollCountRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) {
        window.clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const callbackRefs = useRef<{
    onComplete: (evaluation: ReferenceEvaluationFromApi) => void;
    onError: (error: string) => void;
  }>({
    onComplete: () => {},
    onError: () => {},
  });

  callbackRefs.current = {
    onComplete: onComplete ?? (() => {}),
    onError: onError ?? (() => {}),
  };

  // Fetch evaluation by ID
  const fetchEvaluation = useCallback(
    async (evaluationId: number) => {
      try {
        const response = await getReferenceEvaluation(evaluationId);
        if (!mountedRef.current) return null;

        const evaluation = response.evaluation;

        setState((prev) => ({
          ...prev,
          evaluation,
          error: evaluation.error_message ?? null,
        }));

        // Check if completed
        if (COMPLETED_STATUSES.includes(evaluation.status)) {
          setState((prev) => ({ ...prev, isPolling: false }));
          if (evaluation.status === 'completed') {
            // Only call onComplete if we had to poll for the evaluation
            if (pollCountRef.current > 0) {
              callbackRefs.current.onComplete(evaluation);
            }
          } else if (evaluation.status === 'error') {
            callbackRefs.current.onError(
              evaluation.error_message || 'Unknown error',
            );
          }
        }

        return evaluation;
      } catch (err) {
        if (!mountedRef.current) return null;

        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch evaluation';
        setState((prev) => ({
          ...prev,
          error: errorMessage,
          isPolling: false,
        }));
        callbackRefs.current.onError(errorMessage);
        return null;
      }
    },
    [callbackRefs],
  );

  // Start polling for updates
  const startPolling = useCallback(
    (evaluationId: number) => {
      setState((prev) => ({ ...prev, isPolling: true }));

      const poll = async () => {
        if (!mountedRef.current) return;

        const evaluation = await fetchEvaluation(evaluationId);
        pollCountRef.current++;

        if (
          mountedRef.current &&
          evaluation &&
          !COMPLETED_STATUSES.includes(evaluation.status)
        ) {
          pollTimeoutRef.current = window.setTimeout(poll, pollInterval);
        }
      };

      poll();
    },
    [fetchEvaluation, pollInterval],
  );

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setState((prev) => ({ ...prev, isPolling: false }));
  }, []);

  // Upload a document and start evaluation
  const uploadDocument = useCallback(
    async (file: File) => {
      setState((prev) => ({
        ...prev,
        isUploading: true,
        error: null,
        evaluation: null,
      }));

      try {
        const response = await createReferenceEvaluation(file);
        if (!mountedRef.current) return;

        const evaluation = response.evaluation;

        setState((prev) => ({
          ...prev,
          evaluation,
          isUploading: false,
        }));

        // Start polling for updates
        startPolling(evaluation.id);

        // Update URL with evaluation ID
        const url = new URL(window.location.href);
        url.searchParams.set('evaluation_id', evaluation.id.toString());
        window.history.replaceState({}, '', url.toString());
      } catch (err) {
        if (!mountedRef.current) return;

        const errorMessage =
          err instanceof Error ? err.message : 'Failed to upload document';
        setState((prev) => ({
          ...prev,
          isUploading: false,
          error: errorMessage,
        }));
        callbackRefs.current.onError(errorMessage);
      }
    },
    [startPolling, callbackRefs],
  );

  // Load initial evaluation if ID provided
  useEffect(() => {
    if (initialEvaluationId) {
      fetchEvaluation(initialEvaluationId).then((evaluation) => {
        if (evaluation && !COMPLETED_STATUSES.includes(evaluation.status)) {
          startPolling(initialEvaluationId);
        }
      });
    }
  }, [initialEvaluationId, fetchEvaluation, startPolling]);

  // Clear error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Reset by navigating to the page without evaluation_id
  const reset = useCallback(() => {
    stopPolling();
    // Navigate to the page without evaluation_id to get a clean state
    const url = new URL(window.location.href);
    url.searchParams.delete('evaluation_id');
    window.location.href = url.toString();
  }, [stopPolling]);

  return {
    ...state,
    uploadDocument,
    fetchEvaluation,
    startPolling,
    stopPolling,
    clearError,
    reset,
  };
}
